import React, { useEffect, useMemo, useState } from "react";
import { Form, Divider, Tabs, Button } from "antd";

import { apiGetModelsList } from "api/apiAiModels";
import { apiAnalyzePredictions } from "api/apiPredictions";

import ChooseModel from "components/AiMagician/ChooseModel";
import ResultsHistory from "components/AiMagician/ResultsHistory";
import OptionsSignalSection from "./OptionsSignalSection";

import "components/AiMagician/AiMagician.scss";

const AiOptionsAnalysis = () => {
  const [form] = Form.useForm();
  const [modelsList, setModelsList] = useState([]);
  const [resolution, setResolution] = useState("1d");
  const [resultsHistory, setResultsHistory] = useState([]);
  const [shownPredictionResults, setShownPredictionResults] = useState([]);
  const [loadedResults, setLoadedResults] = useState([]);
  const [selectedResultTab, setSelectedResultTab] = useState("");

  const selectedIds = useMemo(
    () =>
      loadedResults.reduce((acc, item) => {
        acc[item._id] = 1;
        return acc;
      }, {}),
    [loadedResults],
  );

  const getModelsList = async (resolution) => {
    const response = await apiGetModelsList(resolution);
    setModelsList(response);
  };

  const prepareFormData = (models) => {
    const values = form.getFieldsValue();
    const modelIds = models.map((item) => item._id);

    return {
      symbols: values.symbols,
      models: modelIds,
      startDate: values.dates[0].format("YYYY-MM-DD"),
      endDate: values.dates[1].add(1, "d").format("YYYY-MM-DD"),
    };
  };

  const handleCheckAiSymbol = async (model) => {
    await form.validateFields();
    let selectedModelsList = [model];
    if (!model) {
      selectedModelsList = modelsList;
    }

    const task = prepareFormData(selectedModelsList);
    const results = await apiAnalyzePredictions(task);

    setResultsHistory((prevState) => [...results, ...prevState]);
    form.setFieldValue("symbol", []);
  };

  const onLoadAiResult = (result) => {
    // No predictions fetch here -- OptionsSignalSection asks the backend for
    // the already-summarized analysis directly (symbol/model/date-range is
    // all the history row already carries), so the raw prediction table
    // never has to cross the wire just to open a tab.
    setLoadedResults((prevState) => {
      const isLoaded = prevState.find((item) => item._id === result._id);
      if (isLoaded) {
        return prevState;
      }
      return [result, ...prevState];
    });
    setSelectedResultTab(result._id);
  };

  const onRemoveAiResult = (resultId) => {
    setLoadedResults((prevState) =>
      prevState.filter((item) => item._id !== resultId),
    );
    if (resultId === selectedResultTab) {
      setSelectedResultTab(loadedResults[0]?._id);
    }
  };

  const clearLoadedResults = () => {
    setLoadedResults([]);
  };

  useEffect(() => {
    getModelsList(resolution);
  }, [resolution]);

  useEffect(() => {
    document.title = "Options Analysis | Dindicator Dashboard";
  }, []);

  const items = useMemo(
    () =>
      loadedResults.map((item) => ({
        key: item._id,
        label: `${item.symbol} - %${item.tp.toFixed(2)} - ${item.model.name}`,
        children: <OptionsSignalSection results={item} />,
      })),
    [loadedResults],
  );

  const onEdit = (targetKey, action) => {
    if (action === "remove") {
      onRemoveAiResult(targetKey);
    }
  };

  return (
    <div className="ai-magician-wrapper">
      <Divider orientation="left">Options Analysis</Divider>

      <div className="cards-wrapper">
        <ChooseModel
          form={form}
          modelsList={modelsList}
          getModelsList={getModelsList}
          handleCheckAiSymbol={handleCheckAiSymbol}
          resolution={resolution}
          setResolution={setResolution}
        />
      </div>

      <ResultsHistory
        history={resultsHistory || []}
        onLoadAiResult={onLoadAiResult}
        setResultsHistory={setResultsHistory}
        modelsList={modelsList}
        onRemoveAiResult={onRemoveAiResult}
        selectedIds={selectedIds}
        setShownPredictionResults={setShownPredictionResults}
        shownPredictionResults={shownPredictionResults}
      />

      {loadedResults.length > 0 && (
        <div style={{ minHeight: 800 }}>
          <Tabs
            type="editable-card"
            items={items}
            onEdit={onEdit}
            hideAdd
            tabBarExtraContent={{
              right: (
                <Button type="primary" onClick={clearLoadedResults}>
                  Clear
                </Button>
              ),
            }}
            activeKey={selectedResultTab}
            onChange={setSelectedResultTab}
          />
        </div>
      )}
    </div>
  );
};

export default AiOptionsAnalysis;
